import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
import Tabs from '../../components/ui/Tabs';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Pagination from '../../components/ui/Pagination';

// 3D ribbon medal PNG'leri (iframe app'in figma asset'lerinden kopyalandi)
import medalGold   from '../../assets/medals/medal-gold.png';
import medalSilver from '../../assets/medals/medal-silver.png';
import medalBronze from '../../assets/medals/medal-bronze.png';

import {
  fetchLeaderboardSks,
  fetchLeaderboardManager,
  fetchUserDetail,
  setTab,
  setPeriod,
  exportLeaderboard,
} from '../../store/slices/leaderboardSlice';
import { fetchPeriods, selectDefaultPeriod } from '../../store/slices/periodsSlice';
import { fetchMudurlukler } from '../../store/slices/referencesSlice';
import { pushToast } from '../../store/slices/uiSlice';
import { formatASPara } from '../../utils/format';
import apiClient from '../../services/apiClient';
import { Icon } from '../../components/ui/Icon';
import { postToParent } from '../../utils/iframe';


/**
 * Bir donemin reward_template'inden uygun rank kademesini bulur.
 * Tahmini ASPara gosteriminde "ilk N siraya odul verilir" kuralini uygulamak
 * icin kullanilir. Tier bulunmazsa null doner — UI bunu "—" olarak gosterir.
 *
 * tiers: list of { rank_from, rank_to, aspara, ... }
 * rank: kullanicinin (genel veya grup ici) sirasi
 */
function findTierForRank(tiers, rank) {
  if (!Array.isArray(tiers) || rank == null) return null;
  for (const t of tiers) {
    const from = Number(t.rank_from);
    const to = Number(t.rank_to ?? t.rank_from);
    if (rank >= from && rank <= to) return t;
  }
  return null;
}

/**
 * Aktif gorunume gore gecerli tier listesini doner (genel / bolge).
 * per_mudurluk override ham tablodaki butun bolgeleri kapsamadigi icin
 * burada uygulanmiyor — global default'lar yeterli; ileride mudurluk-bazli
 * override gerekirse genisletilebilir.
 */
function tiersForView(rt, view, tab) {
  if (!rt) return [];
  if (tab === 'manager') return rt.manager_tiers || [];
  const _legacyScope = (rt.scope || 'bolge').toLowerCase();
  const genelTiers = rt.genel_tiers ?? (_legacyScope === 'genel' ? (rt.tiers || []) : []);
  const bolgeTiers = rt.bolge_tiers ?? (_legacyScope === 'bolge' ? (rt.tiers || []) : []);
  return view === 'grupici' ? bolgeTiers : genelTiers;
}

/* ── Medal icons (3D ribbon PNG — iframe app ile birebir aynı görsel) ── */
const MEDAL_MAP = {
  1: medalGold,
  2: medalSilver,
  3: medalBronze,
};
function MedalIcon({ rank, size = 56 }) {
  const src = MEDAL_MAP[rank] ?? medalBronze;
  return (
    <img
      src={src}
      alt={rank + '. sıra madalyası'}
      className="object-contain shrink-0"
      style={{ height: size + 'px', width: 'auto' }}
      draggable={false}
    />
  );
}

/* ── Ödül Kademeleri Panel ── */
function OdulKademeleriPanel({ period, sksView, activeTab }) {
  const [badges, setBadges] = useState([]);

  useEffect(() => {
    apiClient.get('/rule-engine/badges')
      .then((r) => setBadges(r.data?.data ?? r.data ?? []))
      .catch(() => {});
  }, []);

  if (!period) return null;

  const rt = period.reward_template || {};
  const isManager = activeTab === 'manager';

  // --- Yönetici sekmesi: manager_tiers kullan ---
  // --- SKS sekmesi: genel / bölge içi tiers ---
  let tiers, badgeAwardsList, panelTitle, panelSubtitle;

  if (isManager) {
    tiers = rt.manager_tiers || [];
    panelTitle = 'Yönetici Ödülleri';
    panelSubtitle = 'Yönetici HYO sıralaması';
    const rawBA = rt.badge_awards;
    badgeAwardsList = Array.isArray(rawBA)
      ? rawBA.filter((a) => a.scope === 'manager' || a.scope === 'Yönetici')
      : (rawBA?.manager || []);
  } else {
    const _legacyScope = (rt.scope || 'bolge').toLowerCase();
    const genelTiers = rt.genel_tiers ?? (_legacyScope === 'genel' ? (rt.tiers || []) : []);
    const bolgeTiers = rt.bolge_tiers ?? (_legacyScope === 'bolge' ? (rt.tiers || []) : []);
    tiers = sksView === 'grupici' ? bolgeTiers : genelTiers;
    panelTitle = 'Ödül Kademeleri';
    panelSubtitle = sksView === 'grupici' ? 'Grup İçi' : 'Genel';
    const rawBA = rt.badge_awards;
    if (Array.isArray(rawBA)) {
      badgeAwardsList = rawBA;
    } else if (rawBA && typeof rawBA === 'object') {
      const scopeKey = sksView === 'grupici' ? 'bolge' : 'genel';
      badgeAwardsList = rawBA[scopeKey] || [];
    } else {
      badgeAwardsList = [];
    }
  }

  // Map rank -> badge_id
  const rankBadgeMap = {};
  (badgeAwardsList || []).forEach((a) => {
    for (let r = a.rank_from; r <= a.rank_to; r++) {
      rankBadgeMap[r] = a.badge_id;
    }
  });

  // Badge name lookup helper
  const getBadgeName = (badgeId) => {
    const b = badges.find((b) => b.id === badgeId);
    return b?.name || null;
  };

  // Tüm tier kademeleri rank_from'a göre sıralı
  const sortedTiers = [...tiers].sort((a, b) => Number(a.rank_from) - Number(b.rank_from));
  const hasAnyTier = sortedTiers.length > 0;

  // Hangi sıraya kadar ödül var → footer metni dinamik
  const maxAwardedRank = tiers.length > 0
    ? Math.max(...tiers.map((t) => t.rank_to || 0))
    : 0;
  const footerText = isManager
    ? `Yönetici ASPara ödülü yalnızca ilk ${maxAwardedRank > 0 ? maxAwardedRank : '?'} sıraya verilir.`
    : `ASPara ödülü ve rozet yalnızca ilk ${maxAwardedRank > 0 ? maxAwardedRank : '?'} sıraya verilir.`;

  // Rank aralığı etiket yardımcısı: "1." veya "1–3." gibi
  const rankRangeLabel = (from, to) =>
    from === to ? `${from}.` : `${from}–${to}.`;

  // Medal renkleri — ilk 3 rank_from için altın/gümüş/bronz, geri kalanlar nötr
  const MEDAL_THEMES = [
    { outer: '#FEF3C7', ring: '#F59E0B', inner: '#FBBF24', number: '#92400E' }, // 1.
    { outer: '#F1F5F9', ring: '#94A3B8', inner: '#CBD5E1', number: '#334155' }, // 2.
    { outer: '#FEF9EE', ring: '#CD7F32', inner: '#E0953A', number: '#7C3A00' }, // 3.
  ];

  return (
    <div
      className="flex-shrink-0 w-[340px] bg-white border border-[#EAECF0] rounded-2xl overflow-hidden"
      style={{ alignSelf: 'flex-start' }}
    >
      {/* Header */}
      <div className="px-5 pt-5 pb-3 border-b border-[#EAECF0]">
        <p className="text-base font-semibold text-[#1875F0]">{panelTitle}</p>
        <p className="text-[11px] text-neutral-400 mt-0.5">
          {panelSubtitle} · {tiers.length} kademe · İlk {maxAwardedRank > 0 ? maxAwardedRank : '?'} sıra
        </p>
      </div>

      {/* Tier rows — tüm kademeler gösterilir */}
      {!hasAnyTier ? (
        <div className="flex flex-col items-center justify-center py-10 px-5 text-center gap-1.5">
          <Icon name="trophy" className="h-7 w-7 text-neutral-200" />
          <p className="text-xs text-neutral-400">Bu dönem için ödül kademe tanımlanmamış.</p>
        </div>
      ) : (
        <div className="max-h-[360px] overflow-y-auto">
          {sortedTiers.map((tier, i) => {
            const from = Number(tier.rank_from);
            const to = Number(tier.rank_to ?? tier.rank_from);
            const theme = MEDAL_THEMES[i] || { outer: '#F0F4FF', ring: '#1875F0', inner: '#4A90E2', number: '#fff' };
            // Rozet: from === to → tekil rank, birden fazlaysa sadece from'a bak
            const badgeId = rankBadgeMap[from];
            const badgeName = badgeId ? getBadgeName(badgeId) : null;
            const amount = Number(tier.aspara || 0);
            // Tekil ilk 3 sira → iframe app'in 3D ribbon medal PNG'sini kullan
            // Coklu ya da rank>3 → eski daire SVG fallback (genis kademeler icin)
            const useRibbonMedal = from === to && from >= 1 && from <= 3;
            return (
              <div key={i}>
                {i > 0 && <div className="h-px bg-[#EAECF0] mx-4" />}
                <div className="flex items-center gap-4 px-5 py-3.5">
                  {/* Mini madalya / sıra rozeti */}
                  <div className="flex-shrink-0">
                    {useRibbonMedal ? (
                      <MedalIcon rank={from} />
                    ) : (
                      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                        <circle cx="24" cy="24" r="24" fill={theme.outer} />
                        <circle cx="24" cy="24" r="20" fill={theme.ring} />
                        <circle cx="24" cy="24" r="16" fill={theme.inner} />
                        <text x="24" y="29" textAnchor="middle" fill={theme.number}
                          fontSize={from === to ? "14" : "11"} fontWeight="800"
                          fontFamily="'Work Sans', Arial, sans-serif">
                          {from === to ? from : `${from}-${to}`}
                        </text>
                      </svg>
                    )}
                  </div>
                  {/* İçerik */}
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-medium text-[#1875F0] mb-0.5">
                      {rankRangeLabel(from, to)} Sıra Ödülü
                    </p>
                    <p className="text-sm font-bold text-[#1875F0] leading-tight">
                      {amount > 0
                        ? amount.toLocaleString('tr-TR') + ' ASPara'
                        : <span className="text-neutral-400 font-normal text-xs">Miktar belirtilmemiş</span>}
                      {/* "+ {rozet adı} Rozeti" iframe app ile birebir gosterim */}
                      {badgeName && (
                        <span className="ml-1 font-semibold">+ {badgeName} Rozeti</span>
                      )}
                    </p>
                  </div>
                  {/* ASPara miktarı — sağ tarafta büyük gösterim */}
                  {amount > 0 && (
                    <div className="flex-shrink-0 text-right">
                      <span className="inline-flex items-center rounded-full bg-success-50 px-2 py-0.5 text-[11px] font-semibold text-success-700">
                        {amount.toLocaleString('tr-TR')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-start gap-2 px-5 py-3 bg-[#F8FAFF] border-t border-[#EAECF0]">
        <Icon name="info" className="h-3.5 w-3.5 text-[#1875F0] mt-0.5 flex-shrink-0" />
        <p className="text-[11px] text-neutral-500 leading-relaxed">
          {footerText}
        </p>
      </div>
    </div>
  );
}

function RankBadge({ rank }) {
  if (rank <= 3) {
    const cls = rank === 1 ? 'rank-gold' : rank === 2 ? 'rank-silver' : 'rank-bronze';
    return (
      <span className="inline-flex w-10 items-center justify-center">
        <span className={`${cls} text-sm`}>#{rank}</span>
      </span>
    );
  }
  return (
    <span className="inline-flex w-10 items-center justify-center text-sm font-semibold text-neutral-500">
      #{rank}
    </span>
  );
}

/** Auth-korumalı rozet görseli (Bearer header ile blob çek) */
function BadgeImg({ badgeId, name }) {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    if (!badgeId) return;
    let url;
    const ctrl = new AbortController();
    apiClient
      .get(`/rule-engine/badges/${badgeId}/image`, { responseType: 'blob', signal: ctrl.signal })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => {});
    return () => { ctrl.abort(); if (url) URL.revokeObjectURL(url); };
  }, [badgeId]);

  if (!src) return <div className="h-10 w-10 rounded-lg bg-neutral-100 animate-pulse" />;
  return <img src={src} alt={name} className="h-10 w-10 object-contain rounded-lg border border-neutral-200 bg-white p-0.5" />;
}

/** Tier progress bar — SKS görev satırı için */
function TierProgressBar({ t }) {
  const raw = t.raw_value ?? 0;
  const cur = t.tier_threshold;
  const next = t.next_tier_threshold;
  let pct = 0;
  if (next != null) {
    const base = cur ?? 0;
    pct = Math.max(0, Math.min(((raw - base) / (next - base)) * 100, 100));
  } else if (cur != null) {
    pct = 100;
  }
  const barColor = cur != null ? 'bg-primary-500' : 'bg-amber-400';
  return (
    <div className="mt-1 space-y-0.5">
      <div className="flex items-center justify-between text-[10px] text-neutral-400">
        <span>{cur != null ? `Eşik: ${cur.toLocaleString('tr-TR')}` : 'Henüz eşik yok'}</span>
        {next != null
          ? <span className="text-amber-600 font-medium">Sonraki: {next.toLocaleString('tr-TR')} (+{t.next_tier_points?.toFixed(1)}p)</span>
          : <span className="text-success-600 font-medium">Maks. tier ✓</span>}
      </div>
      <div className="h-1.5 rounded-full bg-neutral-100 overflow-hidden">
        <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/** Yönetici HYO dashboard */
function ManagerHyoDashboard({ md }) {
  if (!md) return <p className="text-xs text-neutral-400 py-4 text-center">Yönetici verisi bulunamadı.</p>;
  const hyoPct = md.hyo_yuzde ?? 0;
  const minHyo = md.min_hyo_baraj ?? 0;
  const barMax = Math.max(hyoPct, minHyo, 100);
  const hyoBarPct = Math.min((hyoPct / barMax) * 100, 100);
  const minBarPct = Math.min((minHyo / barMax) * 100, 100);
  const hyoColor = hyoPct >= minHyo ? 'bg-success-500' : 'bg-red-400';
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-neutral-200 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold text-neutral-700">Hedef Yüzdesi Oranı (HYO)</p>
          <span className={`text-lg font-bold ${hyoPct >= minHyo ? 'text-success-700' : 'text-red-600'}`}>%{hyoPct.toFixed(1)}</span>
        </div>
        <div className="relative h-3 rounded-full bg-neutral-100 overflow-visible">
          <div className={`h-full rounded-full ${hyoColor}`} style={{ width: `${hyoBarPct}%` }} />
          {minHyo > 0 && (
            <div className="absolute top-0 h-full w-0.5 bg-red-400" style={{ left: `${minBarPct}%` }} title={`Baraj: %${minHyo}`} />
          )}
        </div>
        <div className="flex items-center justify-between text-[10px] text-neutral-400">
          <span>Gerçekleşen: <strong className="text-neutral-700">{md.gerceklesen_puan?.toLocaleString('tr-TR')}</strong></span>
          <span>Hedef: <strong className="text-neutral-700">{md.hedef_puan?.toLocaleString('tr-TR')}</strong></span>
          {minHyo > 0 && <span className="text-red-400">Min. baraj: %{minHyo}</span>}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-neutral-50 px-4 py-3 text-center">
          <p className="text-[10px] text-neutral-400 uppercase tracking-wide">Toplam SKS</p>
          <p className="text-2xl font-bold text-neutral-800 mt-0.5">{md.sks_count}</p>
          {md.min_sks_baraj != null && <p className="text-[10px] text-neutral-400 mt-0.5">Min. baraj: {md.min_sks_baraj}</p>}
        </div>
        <div className={`rounded-xl px-4 py-3 text-center ${md.baraj_gecen_sks >= (md.min_sks_baraj ?? 0) ? 'bg-success-50' : 'bg-red-50'}`}>
          <p className={`text-[10px] uppercase tracking-wide ${md.baraj_gecen_sks >= (md.min_sks_baraj ?? 0) ? 'text-success-500' : 'text-red-400'}`}>
            Baraj Geçen
          </p>
          <p className={`text-2xl font-bold mt-0.5 ${md.baraj_gecen_sks >= (md.min_sks_baraj ?? 0) ? 'text-success-700' : 'text-red-600'}`}>
            {md.baraj_gecen_sks}
            <span className="text-sm font-normal text-neutral-400 ml-1">/ {md.sks_count}</span>
          </p>
        </div>
      </div>
    </div>
  );
}

/** Puan detay + ödül modalı */
function UserScoreDetailModal({ open, onClose, detail, loading, tab, groupRank, period, sksView }) {
  if (!open) return null;

  const snap = detail?.snapshot;
  const taskScores = detail?.task_scores ?? [];
  const managerDetail = detail?.manager_detail ?? null;
  const badgeAwards = detail?.badge_awards ?? [];
  const isManager = tab === 'manager';

  // Tahmini ASPara: backend'in `estimated_aspara` degeri yerine donem
  // reward_template'inden tier-bazli odul gosterilir. Sira odul kademesi
  // disindaysa "—" yazilir.
  const rt = period?.reward_template || {};
  const aspViewTab = isManager ? 'manager' : 'sks';
  const aspTiers = tiersForView(rt, sksView, aspViewTab);
  // SKS icin: grupici view'da groupRank, genel view'da snap.rank
  // Yonetici icin: tum siralama genel — manager_tiers genel rank'a gore
  const aspRank = isManager
    ? (snap?.rank ?? null)
    : (sksView === 'grupici' ? (groupRank ?? snap?.rank_bolge ?? null) : (snap?.rank ?? null));
  const aspTier = findTierForRank(aspTiers, aspRank);
  const aspBelowBaraj = !!snap?.below_baraj;
  const aspAmount = aspTier ? Number(aspTier.aspara || 0) : 0;
  const showAspara = !!aspTier && !aspBelowBaraj && aspAmount > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.4)' }}
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-neutral-200">
          <div>
            <p className="text-base font-semibold text-neutral-900">{snap?.user_name_masked || '—'}</p>
            <p className="text-xs text-neutral-400 mt-0.5">
              {snap?.user_id}&nbsp;·&nbsp;
              {snap?.rank != null && <span>Genel Sıra: <span className="font-semibold text-neutral-700">#{snap.rank}</span></span>}
              {!isManager && groupRank != null && (
                <span className="ml-2">Grup Sırası: <span className="font-semibold text-primary-600">#{groupRank}</span></span>
              )}
            </p>
          </div>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600 transition-colors">
            <Icon name="x" className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-6 w-6 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
            </div>
          ) : (
            <>
              {/* SKS özet kartları */}
              {!isManager && snap && (
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-xl bg-primary-50 px-4 py-3 text-center">
                    <p className="text-[10px] text-primary-500 uppercase tracking-wide">Toplam Puan</p>
                    <p className="text-xl font-bold text-primary-700 mt-0.5">{snap.total_points?.toFixed(1)}</p>
                  </div>
                  <div className={`rounded-xl px-4 py-3 text-center ${showAspara ? 'bg-success-50' : 'bg-neutral-50'}`}>
                    <p className={`text-[10px] uppercase tracking-wide ${showAspara ? 'text-success-500' : 'text-neutral-400'}`}>Tahmini ASPara</p>
                    <p className={`text-xl font-bold mt-0.5 ${showAspara ? 'text-success-700' : 'text-neutral-400'}`}>
                      {showAspara ? formatASPara(aspAmount) : '—'}
                    </p>
                  </div>
                  <div className={`rounded-xl px-4 py-3 text-center ${snap.below_baraj ? 'bg-red-50' : 'bg-neutral-50'}`}>
                    <p className={`text-[10px] uppercase tracking-wide ${snap.below_baraj ? 'text-red-400' : 'text-neutral-400'}`}>Baraj</p>
                    <p className={`text-base font-bold mt-0.5 ${snap.below_baraj ? 'text-red-600' : 'text-neutral-600'}`}>
                      {snap.below_baraj ? 'Baraj Altı' : 'Baraj Üstü'}
                    </p>
                  </div>
                </div>
              )}

              {/* MANAGER — Sıra + ASPara özet */}
              {isManager && snap && (
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-xl bg-primary-50 px-4 py-3 text-center">
                    <p className="text-[10px] text-primary-500 uppercase tracking-wide">Genel Sıra</p>
                    <p className="text-xl font-bold text-primary-700 mt-0.5">#{snap.rank}</p>
                  </div>
                  <div className={`rounded-xl px-4 py-3 text-center ${showAspara ? 'bg-success-50' : 'bg-neutral-50'}`}>
                    <p className={`text-[10px] uppercase tracking-wide ${showAspara ? 'text-success-500' : 'text-neutral-400'}`}>Tahmini ASPara</p>
                    <p className={`text-xl font-bold mt-0.5 ${showAspara ? 'text-success-700' : 'text-neutral-400'}`}>
                      {showAspara ? formatASPara(aspAmount) : '—'}
                    </p>
                  </div>
                  <div className={`rounded-xl px-4 py-3 text-center ${snap.below_baraj ? 'bg-red-50' : 'bg-neutral-50'}`}>
                    <p className={`text-[10px] uppercase tracking-wide ${snap.below_baraj ? 'text-red-400' : 'text-neutral-400'}`}>Baraj</p>
                    <p className={`text-base font-bold mt-0.5 ${snap.below_baraj ? 'text-red-600' : 'text-neutral-600'}`}>
                      {snap.below_baraj ? 'Baraj Altı' : 'Baraj Üstü'}
                    </p>
                  </div>
                </div>
              )}

              {/* MANAGER — HYO dashboard */}
              {isManager && <ManagerHyoDashboard md={managerDetail} />}

              {/* SKS — Görev bazlı puanlar + tier progress */}
              {!isManager && (
                <div>
                  <p className="text-xs font-semibold text-neutral-700 mb-2">Görev Bazlı Puanlar</p>
                  {taskScores.length === 0 ? (
                    <p className="text-xs text-neutral-400 py-3 text-center">Henüz puan yok</p>
                  ) : (
                    <div className="rounded-xl border border-neutral-200 overflow-hidden">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-neutral-50 border-b border-neutral-200">
                            <th className="px-3 py-2 text-left font-semibold text-neutral-600">Görev</th>
                            <th className="px-3 py-2 text-right font-semibold text-neutral-600">Ham / Eşik</th>
                            <th className="px-3 py-2 text-right font-semibold text-neutral-600">Puan</th>
                            <th className="px-3 py-2 text-right font-semibold text-neutral-600">Bonus</th>
                            <th className="px-3 py-2 text-right font-semibold text-neutral-600">Toplam</th>
                            <th className="px-3 py-2 text-right font-semibold text-neutral-600">Güncelleme</th>
                          </tr>
                        </thead>
                        <tbody>
                          {taskScores.map((t) => (
                            <tr key={t.task_id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50">
                              <td className="px-3 py-2 max-w-[160px]">
                                <p className="font-medium text-neutral-800 truncate">{t.task_name}</p>
                                <p className="text-[10px] text-neutral-400">{t.metric_type}</p>
                              </td>
                              <td className="px-3 py-2 text-right align-top">
                                <p className="font-medium text-neutral-700">
                                  {t.raw_value != null ? t.raw_value.toLocaleString('tr-TR') : '—'}
                                </p>
                                <TierProgressBar t={t} />
                              </td>
                              <td className="px-3 py-2 text-right text-neutral-700 align-top">{t.points?.toFixed(1)}</td>
                              <td className="px-3 py-2 text-right text-primary-600 align-top">
                                {t.bonus_points > 0 ? `+${t.bonus_points.toFixed(1)}` : '—'}
                              </td>
                              <td className="px-3 py-2 text-right font-bold text-primary-700 align-top">{t.total_points?.toFixed(1)}</td>
                              <td className="px-3 py-2 text-right text-neutral-400 align-top">
                                {t.updated_at ? new Date(t.updated_at).toLocaleString('tr-TR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Rozetler */}
              {badgeAwards.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-neutral-700 mb-2">Kazanılacak Rozetler</p>
                  <div className="flex flex-wrap gap-3">
                    {badgeAwards.map((b, i) => (
                      <div key={i} className="flex items-center gap-2 rounded-xl border border-neutral-200 bg-white px-3 py-2">
                        <BadgeImg badgeId={b.badge_id} name={`Rozet #${b.badge_id}`} />
                        <div>
                          <p className="text-[10px] font-semibold text-neutral-700 capitalize">{b.scope} ödülü</p>
                          <p className="text-[10px] text-neutral-400">#{b.rank_from}–#{b.rank_to}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!snap && !loading && (
                <p className="text-xs text-neutral-400 py-8 text-center">Bu kullanıcı için veri bulunamadı.</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Leaderboard() {
  const dispatch = useDispatch();
  const {
    sks,
    manager,
    activeTab,
    status,
    periodId,
    sksPagination,
    managerPagination,
  } = useSelector((s) => s.leaderboard);
  const periods = useSelector((s) => s.periods.items);
  const active = useSelector(selectDefaultPeriod);

  // Currently-displayed period object (for OdulKademeleriPanel)
  const currentPeriod = periods.find((p) => p.id === (periodId || active?.id)) || active;

  // Sayfa/size her tab için bağımsız (SKS sayfa 3'deyken Yönetici sayfa 1'de
  // kalmalı). İki tab bağımsız state tutar — tab'a geri dönünce son sayfa
  // korunur.
  const [sksPage, setSksPage] = useState(1);
  const [sksSize, setSksSize] = useState(50);
  const [managerPage, setManagerPage] = useState(1);
  const [managerSize, setManagerSize] = useState(50);

  // SKS alt görünümü: Türkiye Geneli veya Grup İçi (müdürlük bazlı)
  // 2026-05-12: Grup içi sıralama artik tenant-specific musteri musteriligi
  // (sorumlu_mudurluk) bazli — `bolge` yerine `mudurluk`. Dropdown kaynagi
  // GET /rule-engine/mudurlukler (Redux fetchMudurlukler thunk'i ile).
  const [sksView, setSksView] = useState('genel'); // 'genel' | 'grupici'
  const [sksBolge, setSksBolge] = useState('');

  const mudurluklerData   = useSelector((s) => s.references.mudurlukler);
  const mudurluklerStatus = useSelector((s) => s.references.mudurluklerStatus);
  useEffect(() => {
    if (mudurluklerStatus === 'idle') {
      dispatch(fetchMudurlukler({ only_active: true }));
    }
  }, [dispatch, mudurluklerStatus]);

  const bolgeOptions = (mudurluklerData || [])
    .map((m) => m.code)
    .filter(Boolean)
    .sort();

  // Kullanıcı puan detay modalı
  const [detailModal, setDetailModal] = useState({ open: false, data: null, loading: false, groupRank: null });

  useEffect(() => {
    dispatch(fetchPeriods());
  }, [dispatch]);

  // Dönem değişince iki tab için de sayfayı başa al (slice'ta da aynısı
  // yapılıyor — UI state'i onunla senkron kalması için burada da reset).
  useEffect(() => {
    setSksPage(1);
    setManagerPage(1);
  }, [periodId]);

  useEffect(() => {
    const pid = periodId || active?.id;
    if (!pid) return;
    if (activeTab === 'sks') {
      // Grup içi görünümde bölge seçilmemişse fetch yapma
      if (sksView === 'grupici' && !sksBolge) return;
      const params = { period_id: pid, page: sksPage, size: sksSize };
      if (sksView === 'grupici' && sksBolge) params.bolge = sksBolge;
      dispatch(fetchLeaderboardSks(params));
    } else {
      dispatch(fetchLeaderboardManager({ period_id: pid, page: managerPage, size: managerSize }));
    }
  }, [activeTab, periodId, active, dispatch, sksPage, sksSize, managerPage, managerSize, sksView, sksBolge]);

  // SKS alt görünümü değiştirme — müdürlük listesi Redux'tan otomatik geliyor.
  const handleSksViewChange = (newView) => {
    setSksView(newView);
    setSksPage(1);
    if (newView === 'genel') {
      setSksBolge('');
    }
  };

  // ASPara tahminleri yalnızca dönem CLOSED olduğunda anlamlıdır; aktif
  // dönemde değerler değişeceğinden tabloda gösterilmez.
  const isPeriodClosed = currentPeriod?.status === 'CLOSED';

  // Sayfa içindeki baraj çizgisi — o sayfada "ilk below_baraj=true" satırı
  // hangisi; onun üstüne kesikli çizgi koyuyoruz. Dikkat: paginated'de
  // baraj başka sayfaya denk gelebilir → bu sayfada baraj yoksa çizgi çizilmez.
  const barajIndex = Math.max(
    0,
    (activeTab === 'sks' ? sks : manager).findIndex((r) => r.below_baraj),
  );

  const tabs = [
    {
      key: 'sks',
      label: 'Genel Sıralama',
      count: sksPagination?.total ?? 0,
    },
    {
      key: 'manager',
      label: 'Yönetici Sıralama',
      count: managerPagination?.total ?? 0,
    },
  ];

  const sksColumns = [
    {
      key: 'rank',
      header: sksView === 'grupici' ? 'Grup İçi Sıra' : 'Sıra',
      render: (r, idx) => {
        // Grup içi modda rank_bolge güvenilir değil (evaluation job genel sırayı
        // yazıyor olabilir). Dizi konumundan hesaplanan sıra her zaman doğrudur.
        const rank = sksView === 'grupici'
          ? (sksPage - 1) * sksSize + idx + 1
          : r.rank;
        return <RankBadge rank={rank} />;
      },
    },
    {
      key: 'user',
      header: 'Oyuncu',
      render: (r) => (
        <div>
          <p className="text-sm font-medium text-neutral-900">{r.user_name_masked}</p>
          <p className="text-[10px] text-neutral-500">{r.user_id}</p>
        </div>
      ),
    },
    {
      key: 'mudurluk',
      header: 'Müdürlük',
      render: (r) => <span className="text-xs text-neutral-600">{r.mudurluk || r.bolge || '—'}</span>,
    },
    {
      key: 'points',
      header: 'Toplam Puan',
      render: (r) => (
        <span className="font-bold text-primary-700">{r.total_points?.toFixed(1)}</span>
      ),
    },
    {
      key: 'aspara',
      header: 'ASPara',
      // Tahmini ASPara yalnizca odul kademesindeki siralar icin gosterilir
      // (orn. ilk 3). Diger siralarda "—" yazar. Gosterilen miktar da donem
      // reward_template'inden gelen tier.aspara — backend'in `estimated_aspara`
      // (puan-tabanli + tier kumulatif) degeri sirayla esit olmayabilir; kullanici
      // istegi: "donemde belirtilen rank a gore verileceginden onu gosterelim".
      render: (r, idx) => {
        const rt = currentPeriod?.reward_template || {};
        const tiers = tiersForView(rt, sksView, 'sks');
        const rank = sksView === 'grupici'
          ? ((sksPage - 1) * sksSize + idx + 1)
          : r.rank;
        const tier = findTierForRank(tiers, rank);
        if (!tier || r.below_baraj) {
          return <span className="text-xs text-neutral-400">—</span>;
        }
        const amount = Number(tier.aspara || 0);
        if (amount <= 0) {
          return <span className="text-xs text-neutral-400">—</span>;
        }
        return (
          <span className="font-semibold text-success-700">
            {formatASPara(amount)}
          </span>
        );
      },
    },
    {
      key: 'baraj',
      header: '',
      render: (r) => r.below_baraj && <Badge color="red" size="sm">Baraj Altı</Badge>,
    },
    {
      key: 'detail',
      header: '',
      render: (r, idx) => (
        <button
          type="button"
          onClick={() => openDetail(
            r.user_id,
            sksView === 'grupici' ? (sksPage - 1) * sksSize + idx + 1 : null,
          )}
          className="flex h-7 w-7 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 hover:text-primary-600 transition-colors"
          title="Puan detayını gör"
        >
          <Icon name="eye" className="h-4 w-4" />
        </button>
      ),
    },
  ];

  const managerColumns = [
    {
      key: 'rank',
      header: 'Sıra',
      render: (r) => <RankBadge rank={r.rank} />,
    },
    {
      key: 'manager',
      header: 'Yönetici',
      render: (r) => (
        <div>
          <p className="text-sm font-medium text-neutral-900">{r.manager_name_masked}</p>
          <p className="text-[10px] text-neutral-500">{r.manager_user_id}</p>
        </div>
      ),
    },
    {
      key: 'bolge',
      header: 'Bölge Müdürlüğü',
      render: (r) => <span className="text-xs text-neutral-600">{r.bolge_mudurlugu}</span>,
    },
    {
      key: 'sks_count',
      header: 'SKS',
      render: (r) => <span className="text-sm font-medium">{r.sks_count}</span>,
    },
    {
      key: 'hedef',
      header: 'Hedef Puan',
      render: (r) => <span className="text-sm">{r.hedef_puan}</span>,
    },
    {
      key: 'gerceklesen',
      header: 'Gerçekleşen',
      render: (r) => <span className="text-sm font-medium">{r.gerceklesen_puan}</span>,
    },
    {
      key: 'hyo',
      header: 'HYO %',
      render: (r) => (
        <span
          className={
            'font-bold ' +
            (r.hyo_yuzde >= 85
              ? 'text-success-700'
              : r.hyo_yuzde >= 75
                ? 'text-warning-700'
                : 'text-danger-700')
          }
        >
          %{r.hyo_yuzde.toFixed(1)}
        </span>
      ),
    },
    {
      key: 'aspara',
      header: 'ASPara',
      // Yonetici tahmini ASPara — manager_tiers'a gore kademe icindeyse, degilse "—"
      render: (r) => {
        const rt = currentPeriod?.reward_template || {};
        const tiers = tiersForView(rt, null, 'manager');
        const tier = findTierForRank(tiers, r.rank);
        if (!tier || r.below_baraj) {
          return <span className="text-xs text-neutral-400">—</span>;
        }
        const amount = Number(tier.aspara || 0);
        if (amount <= 0) {
          return <span className="text-xs text-neutral-400">—</span>;
        }
        return (
          <span className="font-semibold text-success-700">
            {formatASPara(amount)}
          </span>
        );
      },
    },
    {
      key: 'baraj',
      header: '',
      render: (r) => r.below_baraj && <Badge color="red" size="sm">Baraj Altı</Badge>,
    },
    {
      key: 'detail',
      header: '',
      render: (r) => (
        <button
          type="button"
          onClick={() => openDetail(r.manager_user_id)}
          className="flex h-7 w-7 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 hover:text-primary-600 transition-colors"
          title="Puan detayını gör"
        >
          <Icon name="eye" className="h-4 w-4" />
        </button>
      ),
    },
  ];

  const currentRows = activeTab === 'sks' ? sks : manager;
  // ASPara sütunu yalnızca dönem CLOSED olduğunda gösterilir; diğer
  // durumlarda değerler henüz kesinleşmediğinden sütun gizlenir.
  const currentCols = (activeTab === 'sks' ? sksColumns : managerColumns)
    .filter((c) => isPeriodClosed || c.key !== 'aspara');

  const rowClass = (r, idx) => {
    if (r.below_baraj) return 'bg-amber-50/50';
    if (idx === barajIndex - 1 && barajIndex > 0)
      return 'border-b-2 border-dashed border-amber-400';
    return '';
  };

  const openDetail = async (userCode, groupRank = null) => {
    const pid = periodId || active?.id;
    if (!pid || !userCode) return;
    setDetailModal({ open: true, data: null, loading: true, groupRank });
    const res = await dispatch(
      fetchUserDetail({ period_id: pid, user_code: userCode, tab: activeTab === 'sks' ? 'SKS' : 'MANAGER' }),
    );
    if (res.meta.requestStatus === 'fulfilled') {
      setDetailModal({ open: true, data: res.payload, loading: false, groupRank });
    } else {
      setDetailModal({ open: false, data: null, loading: false, groupRank: null });
      dispatch(pushToast({ kind: 'error', message: 'Puan detayı alınamadı.' }));
    }
  };

  const handleExport = async () => {
    const pid = periodId || active?.id;
    if (!pid) {
      dispatch(pushToast({ kind: 'error', message: 'Önce bir dönem seçin.' }));
      return;
    }
    const exportParams = { period_id: pid, tab: activeTab.toUpperCase() };
    if (activeTab === 'sks' && sksView === 'grupici' && sksBolge) exportParams.bolge = sksBolge;

    const res = await dispatch(exportLeaderboard(exportParams));

    if (res.meta.requestStatus === 'rejected') {
      // Backend 4xx/5xx — error.message'ı göster
      const detail = res.payload || res.error?.message || '';
      dispatch(pushToast({
        kind: 'error',
        message: detail ? `Excel indirilemedi: ${detail}` : 'Excel indirilemedi.',
      }));
      return;
    }

    // Payload null/undefined ise (success ama içerik yok) — savunmacı
    if (!res.payload) {
      dispatch(pushToast({
        kind: 'error',
        message: 'Excel boş döndü — bu dönem için sıralama verisi yok.',
      }));
      return;
    }

    // Blob normalize — Axios bazen ArrayBuffer döndürebilir
    let blob;
    try {
      if (res.payload instanceof Blob) {
        blob = res.payload;
      } else if (res.payload instanceof ArrayBuffer) {
        blob = new Blob([res.payload], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
      } else {
        blob = new Blob([res.payload], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[export] Blob olusturulamadi', e, res.payload);
      dispatch(pushToast({ kind: 'error', message: 'Excel formati hatali.' }));
      return;
    }

    // Boş blob check — backend hatası blob'a JSON yazıyor olabilir
    if (blob.size === 0) {
      dispatch(pushToast({
        kind: 'error',
        message: 'Excel boş — bu dönem için sıralama verisi yok.',
      }));
      return;
    }

    const filename = `siralama_${activeTab}_donem${pid}.xlsx`;

    // ──────────────────────────────────────────────────────────────────────
    // Iframe-safe download: admin panel spine app içinde iframe olarak embed
    // edildiği için bazı tarayıcılarda <a download> iframe sandbox'ında
    // bloklanır. Önce parent'a postMessage ile devretmeyi dene; parent
    // dinleyici varsa yakalayıp top window'da indirir. Yoksa fallback olarak
    // <a download> + click pattern uygulanır.
    // ──────────────────────────────────────────────────────────────────────

    // Iframe'de mi çalışıyoruz? (top !== self → embed edilmişiz)
    const isInIframe = (() => {
      try {
        return window.top !== window.self;
      } catch {
        // Cross-origin top'a erişim engellenirse → iframe'iz, ama parent
        // farklı origin. postMessage hâlâ çalışır.
        return true;
      }
    })();

    if (isInIframe) {
      // Parent'a blob URL gönder; parent dinliyorsa top window'dan indirir.
      // Parent dinlemiyorsa 200ms sonra fallback'e geç (kullanıcı en azından
      // local download deneyecek).
      try {
        const parentUrl = URL.createObjectURL(blob);
        postToParent('download:file', { url: parentUrl, filename });
        // Iframe'in kendi indirme denemesi — parent dinlemiyorsa burası işler
        triggerDownload(parentUrl, filename);
        // 5sn sonra revoke — parent'in indirme bitirmesi için pencere
        setTimeout(() => URL.revokeObjectURL(parentUrl), 5000);
        dispatch(pushToast({ kind: 'success', message: 'Excel indirildi.' }));
        return;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[export] iframe download postMessage basarisiz, fallback', e);
      }
    }

    // Normal download (top window veya fallback)
    try {
      const url = URL.createObjectURL(blob);
      triggerDownload(url, filename);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      dispatch(pushToast({ kind: 'success', message: 'Excel indirildi.' }));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[export] download tetiklenemedi', e);
      dispatch(pushToast({
        kind: 'error',
        message: 'Excel indirilemedi (tarayıcı izin vermedi). Sayfayı yeni sekmede deneyin.',
      }));
    }
  };

  // <a download> click pattern — iframe-safe
  function triggerDownload(url, filename) {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    // Bazı tarayıcılarda click hemen DOM'dan kaldırılırsa indirme iptal
    // olabilir. Küçük bir gecikmeyle kaldır.
    setTimeout(() => {
      try { document.body.removeChild(a); } catch { /* zaten kaldırılmış */ }
    }, 100);
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Sıralama"
        subtitle="Oyuncu sıralaması — baraj altındakiler ödül almaz"
        className="bg-white border border-[#E1E1E1] shadow-card rounded-[1.25rem] px-6 py-4"
        actions={
          <>
            <Select
              value={periodId || active?.id || ''}
              onChange={(e) => dispatch(setPeriod(Number(e.target.value)))}
              options={periods
                .filter((p) => p.status === 'ACTIVE' || p.status === 'CLOSED')
                .map((p) => ({
                  value: p.id,
                  label: p.status === 'CLOSED'
                    ? `${p.name} (Kapalı)`
                    : p.name,
                }))}
              className="min-w-[12rem]"
            />
            <Button variant="secondary" icon="download" onClick={handleExport}>
              Excel İndir
            </Button>
          </>
        }
      />

      <div className="flex gap-6 items-start">

      <Card padded={false} className="flex-1 min-w-0">
        <div className="px-4 pt-2">
          <Tabs tabs={tabs} value={activeTab} onChange={(k) => dispatch(setTab(k))} />
        </div>

        {/* SKS alt görünümü: Türkiye Geneli / Grup İçi toggle */}
        {activeTab === 'sks' && (
          <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 border-b border-neutral-100 bg-neutral-50/60">
            <div className="flex h-11 rounded-lg border border-neutral-200 overflow-hidden text-xs font-medium">
              <button
                type="button"
                onClick={() => handleSksViewChange('genel')}
                className={`h-full px-3 transition-colors ${
                  sksView === 'genel'
                    ? 'bg-primary-600 text-white'
                    : 'text-neutral-600 hover:bg-neutral-50'
                }`}
              >
                Tümü
              </button>
              <button
                type="button"
                onClick={() => handleSksViewChange('grupici')}
                className={`h-full px-3 transition-colors border-l border-neutral-200 ${
                  sksView === 'grupici'
                    ? 'bg-primary-600 text-white'
                    : 'text-neutral-600 hover:bg-neutral-50'
                }`}
              >
                Grup İçi
              </button>
            </div>
            {sksView === 'grupici' && (
              <Select
                value={sksBolge}
                onChange={(e) => { setSksBolge(e.target.value); setSksPage(1); }}
                options={[
                  { value: '', label: 'Müdürlük seçin…' },
                  ...bolgeOptions.map((b) => ({ value: b, label: b })),
                ]}
                className="min-w-[16rem]"
              />
            )}
            {sksView === 'grupici' && sksBolge && (
              <span className="text-xs text-neutral-500">
                <span className="font-medium text-neutral-700">{currentRows.length}</span> kişi · grup içi sıralama
              </span>
            )}
          </div>
        )}

        {/* Grup içi — bölge seçilmemişse yönlendirici boş durum */}
        {activeTab === 'sks' && sksView === 'grupici' && !sksBolge ? (
          <div className="flex flex-col items-center justify-center py-16 text-center gap-2">
            <Icon name="layers" className="h-8 w-8 text-neutral-300" />
            <p className="text-sm font-semibold text-neutral-600">Grup İçi Sıralama</p>
            <p className="text-xs text-neutral-400 max-w-xs">
              Yukarıdan bir müdürlük seçerek o müdürlüğün grup içi sıralamasını görüntüleyin.
            </p>
          </div>
        ) : (
        <Table
          columns={currentCols}
          rows={currentRows}
          loading={status === 'loading'}
          rowClassName={rowClass}
          emptyMessage={
            status === 'failed'
              ? 'Sıralama verisi alınamadı. Sunucuya ulaşılamıyor — servis yayına alındıktan sonra yeniden deneyin.'
              : 'Henüz sıralama yok. Yeni dönem için Dönemler ekranındaki "Hesapla" butonu ile önce Dry-Run önizlemesi alın, sonra sonuçlandırın.'
          }
        />
        )}
        <div className="px-4 pb-4 pt-2">
          <Pagination
            page={activeTab === 'sks' ? sksPage : managerPage}
            size={activeTab === 'sks' ? sksSize : managerSize}
            total={(activeTab === 'sks' ? sksPagination : managerPagination)?.total ?? 0}
            onPageChange={(p) => {
              if (activeTab === 'sks') setSksPage(p);
              else setManagerPage(p);
            }}
            onSizeChange={(newSize) => {
              if (activeTab === 'sks') {
                setSksSize(newSize);
                setSksPage(1);
              } else {
                setManagerSize(newSize);
                setManagerPage(1);
              }
            }}
            loading={status === 'loading'}
          />
        </div>
        <div className="border-t border-neutral-200 px-4 py-3 bg-neutral-50 text-xs text-neutral-500 flex items-center justify-between">
          <span>
            <span className="inline-block h-2 w-4 align-middle baraj-divider mr-1"></span>
            Baraj çizgisi altındaki oyuncular ödül almaz.
          </span>
          <span>
            Bu sayfada {currentRows.length} oyuncu · Baraj altı: {currentRows.filter((r) => r.below_baraj).length}
          </span>
        </div>
      </Card>

      <OdulKademeleriPanel period={currentPeriod} sksView={sksView} activeTab={activeTab} />

      </div>{/* end flex row */}

      <UserScoreDetailModal
        open={detailModal.open}
        loading={detailModal.loading}
        detail={detailModal.data}
        tab={activeTab === 'sks' ? 'sks' : 'manager'}
        groupRank={detailModal.groupRank}
        period={currentPeriod}
        sksView={sksView}
        onClose={() => setDetailModal({ open: false, data: null, loading: false, groupRank: null })}
      />
    </div>
  );
}
